<script lang="ts" setup>
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import {
  useBrowserStore,
  usePriceCalendarStore,
} from '@apps/main/new-desktop/core/stores';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';
import { EditTripView } from '@apps/main/new-desktop/modules/editTripModal';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { PriceCalendar } from '@apps/main/new-desktop/modules/priceCalendar';
import { PaxTab } from '@apps/main/new-desktop/modules/paxSelector';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useConfigurationPanelStore } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore';

import type { AppTabType } from '@libs/ui';
import { AppSlidePanel, AppStepper } from '@libs/ui';
import type {
  AutoCompleteSuggestion,
  IngredientId,
  Pax,
} from '@libs/trip-planner-interfaces';

interface ConfigurationStepperPanelProps {
  isOpen: boolean;
}

const props = defineProps<ConfigurationStepperPanelProps>();

interface ConfigurationStepperPanelEmits {
  (eventName: 'close'): void;
  (eventName: 'update-pax', newPax: Pax): void;
  (eventName: 'add-destination', suggestion: AutoCompleteSuggestion): void;
  (
    eventName: 'change-destination',
    {
      suggestion,
      ingredientId,
    }: { suggestion: AutoCompleteSuggestion; ingredientId: IngredientId }
  ): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', segment: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
}
const emit = defineEmits<ConfigurationStepperPanelEmits>();

const configurationPanelStore = useConfigurationPanelStore();
const priceCalendarStore = usePriceCalendarStore();
const browserStore = useBrowserStore();

const { t } = i18n.global;
const tabs: AppTabType[] = [
  {
    title: t('planner.configuration_panel.tabs.travelers'),
    icon: 'people',
    key: TabKey.Pax,
  },
  {
    title: t('planner.configuration_panel.tabs.destinations'),
    icon: 'itinerary',
    key: TabKey.Itinerary,
  },
  {
    title: t('planner.configuration_panel.tabs.date'),
    icon: 'calendarOutlined',
    key: TabKey.Calendar,
  },
];

function onSelectMonth(dayInMonth: Date): void {
  priceCalendarStore.selectItineraryMonth(
    dayInMonth,
    CoreEventSource.MonthsSelector
  );
}

function onSelectDate(date: Date): void {
  priceCalendarStore.selectItineraryDate(date, CoreEventSource.MonthsSelector);
}
</script>

<template>
  <AppSlidePanel
    :disable-header="true"
    direction="right"
    :is-open="props.isOpen"
    :width="browserStore.isLargerThanMediumMax ? '33.33%' : '100%'"
    padding="0"
  >
    <template #title>
      {{ $t('planner.configuration_panel.title') }}
    </template>
    <template #body>
      <AppStepper
        :next-step-button-text="t('planner.next')"
        :last-step-button-text="t('planner.show_trip')"
        :steps="tabs"
        @close="emit('close')"
      >
        <template #step-0="{ active }">
          <div
            v-if="active && configurationPanelStore.getPaxDetails"
            class="step-wrapper"
          >
            <PaxTab
              :pax-details="configurationPanelStore.getPaxDetails"
              @update-pax="emit('update-pax', $event)"
            />
          </div>
        </template>
        <template #step-1="{ active }">
          <div
            v-if="active && configurationPanelStore.editTripDetails"
            class="step-wrapper"
          >
            <EditTripView
              :segments="configurationPanelStore.editTripDetails.segments"
              :is-desktop="configurationPanelStore.editTripDetails.isDesktop"
              :initial-step="
                configurationPanelStore.editTripDetails.initialStep
              "
              @add-destination="emit('add-destination', $event)"
              @change-destination="emit('change-destination', $event)"
              @change-trip-order="emit('change-trip-order', $event)"
              @change-number-of-nights="emit('change-number-of-nights', $event)"
              @remove-destination="emit('remove-destination', $event)"
              @change-step="configurationPanelStore.changeEditStep"
              @close="emit('close')"
            />
          </div>
        </template>
        <template #step-2="{ active }">
          <PriceCalendar
            v-if="active"
            :selected-month="priceCalendarStore.selectedMonthOption.id"
            :months="priceCalendarStore.priceRibbonMonths"
            :price-calendar-state="priceCalendarStore.priceCalendarState"
            show-prices
            @select-date="onSelectDate"
            @select-month="onSelectMonth"
          />
        </template>
      </AppStepper>
    </template>
  </AppSlidePanel>
</template>

<style lang="scss" scoped>
.step-wrapper {
  padding: var(--spacing-16);
}
</style>
